import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ExternalLink, Lock, LogOut, Mail, Phone, RefreshCw } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { BUSINESS } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["New", "Contacted", "Quote Sent", "Booked", "Completed", "Cancelled"];

const STATUS_STYLES = {
  New: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Contacted: "bg-sky-100 text-sky-700 border-sky-200",
  "Quote Sent": "bg-amber-100 text-amber-700 border-amber-200",
  Booked: "bg-indigo-100 text-indigo-700 border-indigo-200",
  Completed: "bg-slate-100 text-slate-600 border-slate-200",
  Cancelled: "bg-red-100 text-red-600 border-red-200",
};

const Detail = ({ label, value }) =>
  value ? (
    <div>
      <dt className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium text-navy-800">{value}</dd>
    </div>
  ) : null;

export default function Admin() {
  const [key, setKey] = useState(() => localStorage.getItem("oz_admin_key") || "");
  const [input, setInput] = useState("");
  const [enquiries, setEnquiries] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (k) => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(`${API}/enquiries`, { headers: { "X-Admin-Key": k } });
        setEnquiries(res.data.enquiries);
        localStorage.setItem("oz_admin_key", k);
        setKey(k);
      } catch (e) {
        setEnquiries(null);
        if (e.response?.status === 401) {
          setError("That access key is incorrect. Please try again.");
          localStorage.removeItem("oz_admin_key");
          setKey("");
        } else {
          setError("Couldn't load enquiries. Please try again in a moment.");
        }
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (key) load(key);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (id, status) => {
    setEnquiries((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
    try {
      await axios.patch(`${API}/enquiries/${id}`, new URLSearchParams({ status }), {
        headers: { "X-Admin-Key": key },
      });
    } catch (e) {
      setError("Couldn't update the status. Please refresh and try again.");
    }
  };

  if (!enquiries) {
    return (
      <section className="flex min-h-[80vh] items-center justify-center bg-slate-50 px-5 pt-24 pb-16" data-testid="admin-login">
        <Seo title="Enquiries | Oz SpotNest Admin" description="Private enquiries dashboard." />
        <form
          onSubmit={(e) => {
            e.preventDefault();
            load(input);
          }}
          className="w-full max-w-md rounded-3xl border border-slate-100 bg-white p-10 shadow-2xl shadow-sky-900/5"
        >
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-navy-800 text-white">
            <Lock className="h-6 w-6" />
          </span>
          <h1 className="mt-6 text-center text-2xl font-bold tracking-tight">Enquiries Dashboard</h1>
          <p className="mt-2 text-center text-sm text-slate-500">
            Private area for {BUSINESS.name}. Enter your access key to view cleaning enquiries.
          </p>
          <label htmlFor="admin-key" className="field-label mt-8">Access Key</label>
          <input
            id="admin-key"
            data-testid="admin-key-input"
            type="password"
            autoComplete="current-password"
            className="input-field"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Enter access key"
          />
          {error && (
            <p data-testid="admin-login-error" role="alert" className="mt-3 text-xs font-semibold text-red-600">
              {error}
            </p>
          )}
          <button type="submit" data-testid="admin-login-button" disabled={loading} className="btn-primary mt-6 w-full">
            {loading ? "Checking…" : "View Enquiries"}
          </button>
        </form>
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-slate-50 pt-32 pb-24" data-testid="admin-dashboard">
      <Seo title="Enquiries | Oz SpotNest Admin" description="Private enquiries dashboard." />
      <div className="container-site">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Cleaning Enquiries</h1>
            <p className="mt-2 text-sm text-slate-500" data-testid="admin-enquiry-count">
              {enquiries.length} enquir{enquiries.length === 1 ? "y" : "ies"} received
            </p>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              data-testid="admin-refresh-button"
              onClick={() => load(key)}
              className="btn-outline !px-5 !py-2.5 text-sm"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              type="button"
              data-testid="admin-logout-button"
              onClick={() => {
                localStorage.removeItem("oz_admin_key");
                setKey("");
                setEnquiries(null);
                setInput("");
              }}
              className="btn-outline !px-5 !py-2.5 text-sm"
            >
              <LogOut className="h-4 w-4" /> Log out
            </button>
          </div>
        </div>

        {error && (
          <p data-testid="admin-error" role="alert" className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </p>
        )}

        {enquiries.length === 0 ? (
          <div className="mt-12 rounded-3xl border border-dashed border-slate-200 bg-white p-16 text-center" data-testid="admin-empty-state">
            <p className="font-heading text-xl font-semibold text-navy-800">No enquiries yet</p>
            <p className="mt-2 text-sm text-slate-500">New quote requests from the website will appear here.</p>
          </div>
        ) : (
          <ul className="mt-10 space-y-6">
            {enquiries.map((e, i) => (
              <li
                key={e.id}
                data-testid={`admin-enquiry-${i}`}
                className="rounded-3xl border border-slate-100 bg-white p-7 shadow-lg shadow-sky-900/5 sm:p-8"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-heading text-xl font-bold text-navy-800" data-testid={`admin-enquiry-name-${i}`}>
                      {e.full_name}
                    </p>
                    <p className="mt-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
                      {new Date(e.submitted_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}
                    </p>
                  </div>
                  <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
                    Status
                    <select
                      data-testid={`admin-enquiry-status-${i}`}
                      value={e.status}
                      onChange={(ev) => updateStatus(e.id, ev.target.value)}
                      className={`rounded-full border px-4 py-2 font-heading text-xs font-bold normal-case tracking-normal ${STATUS_STYLES[e.status] || ""}`}
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </label>
                </div>

                <dl className="mt-6 grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 lg:grid-cols-4">
                  <div>
                    <dt className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">Contact</dt>
                    <dd className="mt-1 space-y-1">
                      <a href={`tel:${e.phone}`} data-testid={`admin-enquiry-phone-${i}`} className="flex items-center gap-1.5 text-sm font-semibold text-emerald-700 hover:underline">
                        <Phone className="h-3.5 w-3.5" /> {e.phone}
                      </a>
                      <a href={`mailto:${e.email}`} data-testid={`admin-enquiry-email-${i}`} className="flex items-center gap-1.5 break-all text-sm font-semibold text-sky-700 hover:underline">
                        <Mail className="h-3.5 w-3.5 shrink-0" /> {e.email}
                      </a>
                    </dd>
                  </div>
                  <Detail label="Service" value={e.service} />
                  <Detail label="Property" value={e.property_type} />
                  <Detail label="Frequency" value={e.frequency} />
                  <Detail label="Bedrooms" value={e.bedrooms} />
                  <Detail label="Bathrooms" value={e.bathrooms} />
                  <Detail label="Property size" value={e.property_size} />
                  <Detail
                    label="Preferred"
                    value={[e.preferred_date, e.preferred_time].filter(Boolean).join(" · ")}
                  />
                </dl>

                {e.notes && (
                  <div className="mt-5 rounded-2xl bg-slate-50 p-4 text-sm leading-relaxed text-slate-600">
                    <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">Notes</span>
                    <p className="mt-1">{e.notes}</p>
                  </div>
                )}

                {e.photos?.length > 0 && (
                  <div className="mt-5">
                    <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">
                      Property photos ({e.photos.length})
                    </span>
                    <div className="mt-2 flex flex-wrap gap-3">
                      {e.photos.map((p, j) => (
                        <a
                          key={p}
                          href={`${API}/files/${p}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`admin-enquiry-photo-${i}-${j}`}
                          className="group relative block overflow-hidden rounded-xl ring-1 ring-slate-200"
                        >
                          <img
                            src={`${API}/files/${p}`}
                            alt={`Property photo ${j + 1} from ${e.full_name}`}
                            loading="lazy"
                            className="h-24 w-24 object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                          <ExternalLink className="absolute bottom-1.5 right-1.5 h-3.5 w-3.5 text-white drop-shadow" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
